import { useState, useContext, useRef, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { getClientDomain, getToken, translate, verifyHideCountryItems, setStatusObjTranslate } from '../../../../utils/Common';
import AuthContext from '../../../../contexts/auth';
import ModalContext from '../../../../contexts/modal';
import SearchFilter from '../../../../components/data/SearchFilter';
import PageContainer from '../../../../components/layout/PageContainer';
import PageHeader from '../../../../components/layout/PageHeader';
import ContentCard from '../../../../components/layout/ContentCard';
import Table from '../../../../components/data/Table';
import Pagination from '../../../../components/data/Pagination';
import StatusSummaryCards from './StatusSummaryCards';
import IntegratedFilter from './IntegratedFilter';
import StatusFilterPanel from './StatusFilterPanel';
import { getPropertyPackagesStatus, getPropsTypeOfPackage, formatDatePicker, handlerGetClasificacion, getPackageDetailStatusLabel } from '../../../../utils/Common';
import Badge from '../../../../components/common/Badge';
import SelectorColumns from '../../../../components/ShowTable/SelectorColumns';
import { useColumnConfig } from '../../../../hooks/useColumnConfig';
import { ToastContainer, useToast } from '../../../../components/common/Toast';
import { FaBox } from 'react-icons/fa';
import * as FaIcons from 'react-icons/fa';
import styles from './SearchPackages.module.css';
import { verifyAccessViewPermissions } from '../../../../config/permissions';
import Button from '../../../../components/common/Button';

// vista principal para realizar tracking
const SearchPackages = () => {
    const history = useHistory();
    const location = useLocation();
    const userContext = useContext(AuthContext);
    const modalContext = useContext(ModalContext);
    const canSearch = verifyAccessViewPermissions(["search"], { path: location.pathname });
    const [packagesFound, setPackagesFound] = useState([]);
    const [itemsFiltered, setItemsFiltered] = useState([]);
    const [itemsPerPage, setItemsPerPage] = useState(20);
    const [totalItems, setTotalItems] = useState(0);
    // Starts true: the screen runs a default search on mount, so between the
    // first render and that fetch the list is empty and nothing says it is
    // loading — the "no packages found" message paints and is then replaced.
    const [isLoading, setIsLoading] = useState(true);
    const [statusPkgSelected, setStatusPkgSelected] = useState([]);
    const [packagesDefault, setPackagesDefault] = useState([]);
    const [statusCounts, setStatusCounts] = useState({});
    const [statusPkgDefault] = useState([1, 2, 3]);
    // Todos los estatus posibles: se usa cuando se filtra por tipo de paquete,
    // para no restringir por estatus (el SP no devuelve nada con estatusBoxit vacío).
    const [statusPkgAll] = useState([1, 2, 3, 4, 5, 6, 7]);
    const sectionResults = useRef(null);
    const lastSearchPayloadRef = useRef(null);
    const isInitialMount = useRef(true);
    const lastUrlSearchRef = useRef('');
    
    // Estados para filtrado inline (modo unificado como Companies)
    const [filters, setFilters] = useState({});
    const [statusOptions, setStatusOptions] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedField, setSelectedField] = useState('numeroTracking');
    const [selectedStatus, setSelectedStatus] = useState('all');
    const [isStatusPanelOpen, setIsStatusPanelOpen] = useState(false);
    
    // Estados para almacenar datos de referencia para formatear chips
    const [platformsList, setPlatformsList] = useState([]);
    const [companiesList, setCompaniesList] = useState([]);
    
    // Configuración de columnas
    const { columns, setColumns, visibleColumns, renderers } = useColumnConfig('packages');
    const { toasts, showSuccess, showError, removeToast } = useToast();

    const findPackages = async (data) => {
        try {
            if (!canSearch) return;
            setIsLoading(true);

            let searchData = data;

            const hasAtLeastOneValue = Object.values(searchData).some(
                value => value !== undefined && value !== null && value !== "" && value !== translate("global.select")
            );
            
            if (!hasAtLeastOneValue) {
                setIsLoading(false);
                // En lugar de mostrar modal, simplemente limpiar resultados
                setPackagesFound([]);
                setItemsFiltered([]);
                return;
            }

            // Continuar con el procesamiento
            sectionResults?.current?.scrollIntoView({ behavior: 'smooth' });
    
            let dataToSend = {};
    
            // Validación básica de usuario
            dataToSend.IdUsuario = Number(searchData.usuario);

            // if (!userContext.user?.IsAdmin) {
            //     dataToSend.IdUsuario = Number(userContext.user?.Ruc);
            // } else if (searchData.usuario && searchData.usuario !== "Empresa" && searchData.usuario !== "No hay usuarios") {
            //     dataToSend.IdUsuario = Number(searchData.usuario);
            // }
    
            // Asignación de campos
            const fields = ['nombre', 'apellido', 'numeroPaquete', 'numeroTracking', 'plataforma', 'tipo', 'fechaInicio', 'fechaFin', 'externalId', 'typePkg', 'email', 'reservationId', 'idPaquete'];
            fields.forEach(field => {
                if (field === 'idPaquete') {
                    // El SP recibe @IdPaquete como Numeric, así que solo tiene sentido
                    // con dígitos: se descarta cualquier otro caracter y, si no queda
                    // nada, no se envía el campo (un parseInt(NaN) reventaría el query).
                    const digits = (searchData[field] ?? '').toString().replace(/\D/g, '');
                    if (digits) dataToSend[field] = digits;
                } else if (field === 'typePkg') {
                    const val = searchData[field];
                    // USA: 0, 1, 'dropoff' | Panama/BR: '0,1,2', '3'
                    if (val === 'dropoff') {
                        // Drop Off (solo USA). El SP UspFindTrackingPaquetes aplica
                        // IsDropoff = 1 AND IdTipoPaquete = 1, así que basta con enviar el dropoff;
                        // no se manda typePkg para no duplicar la condición de tipo.
                        // Misma convención que el create package: se envía integrationType="pudo_dropoff".
                        dataToSend.integrationType = "pudo_dropoff";
                    } else if (val === 0 || val === '0' || val === 1 || val === '1') {
                        dataToSend[field] = parseInt(val, 10);
                    } else if (val === '0,1,2' || val === '3') {
                        dataToSend[field] = val;
                    }
                } else if (field === 'plataforma') {
                    const val = searchData[field];
                    if (val !== undefined && val !== null && val !== '') {
                        const numVal = Number(val);
                        // Para el API de USA, plataforma debe ser numérico para pasar la validación _.isNumber
                        dataToSend[field] = Number.isNaN(numVal)
                            ? val.toString().trim().toUpperCase()
                            : numVal;
                    }
                } else if (searchData[field]) {
                    dataToSend[field] = searchData[field].toString().trim().toUpperCase();
                }
            });
    
            // Manejo de estatus
            if (searchData.estatusBoxit && searchData.estatusBoxit !== `${translate("global.select")}` && searchData.estatusBoxit !== '') {
                dataToSend.estatusBoxit = searchData.estatusBoxit;
                // Convertir string separado por comas a array de números
                const parsedStatusIds = searchData.estatusBoxit
                    .toString()
                    .split(',')
                    .map(x => parseInt(x.trim(), 10))
                    .filter(n => !Number.isNaN(n));
                setStatusPkgSelected(parsedStatusIds);
            } else if (!searchData.estatusBoxit) {
                // El SP no devuelve resultados si estatusBoxit va vacío.
                // - Si se filtra por locker, compañía o tipo de paquete NO se restringe por
                //   estatus: se envían todos (la paginación del SP acota el volumen).
                // - En cualquier otro caso se usan los estatus seleccionados o, en su defecto,
                //   los estatus por defecto (evita perder resultados en el primer render).
                const hasAllStatusFilter = ['plataforma', 'usuario', 'typePkg'].some((key) => {
                    const value = searchData[key];
                    return value !== undefined && value !== null && value !== '';
                });
                const statusesToSend = hasAllStatusFilter
                    ? statusPkgAll
                    : (statusPkgSelected.length > 0 ? statusPkgSelected : statusPkgDefault);
                dataToSend.estatusBoxit = statusesToSend.join(",");
            }
            
    
            // Verifica si hay criterios suficientes para buscar (typePkg puede ser 0, '0,1,2', '3', que son válidos)
            const hasCriteria = Object.values(dataToSend).some(val => val !== undefined && val !== null && (val !== '' || val === 0 || val === '0,1,2' || val === '3'));
            if (!hasCriteria) {
                setIsLoading(false);
                // En lugar de mostrar modal, simplemente limpiar resultados
                setPackagesFound([]);
                setItemsFiltered([]);
                return;
            }

            const pageNum = data.pageNumber ?? 1;
            dataToSend.itemsPerPage = itemsPerPage;
            dataToSend.pageNumber = pageNum;
            lastSearchPayloadRef.current = { ...dataToSend };
    
            // Hacer la solicitud
            const response = await fetch(`${getClientDomain(2)}/dashboard/packages/find/tracking`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'authorization': `Bearer ${getToken()}`
                },
                body: JSON.stringify(dataToSend)
            });
    
            const result = await response.json();
    
            if (result.code === 200 && result.payload) {
                setItemsFiltered(result.payload);
                setPackagesFound(result.payload);
                setCurrentPage(pageNum);
                lastSearchPayloadRef.current = { ...dataToSend };
                const fallback = result.payload.length < itemsPerPage ? (pageNum - 1) * itemsPerPage + result.payload.length : pageNum * itemsPerPage;
                setTotalItems(result.totalItems != null ? Number(result.totalItems) : fallback);
                setStatusCounts(result.statusCounts || {});
            } else if (result.code === 404) {
                setItemsFiltered([]);
                setPackagesFound([]);
                setCurrentPage(pageNum);
                setTotalItems(0);
                setStatusCounts({});
            } else {
                showErrorModal();
            }
    
        } catch (error) {
            console.error(error);
            showErrorModal();
        } finally {
            setIsLoading(false);
        }
    };
    
    const showNotFoundModal = () => {
        modalContext.confirm(
            translate("global.alert.attention"),
            <span>{translate("dashboard.packages.search.NOT_PACKAGE_FOUND")}</span>,
            [
                <button
                    key="accept"
                    type="button"
                    variant="outline-secondary" size="sm"
                    onClick={() => {
                        modalContext.setIsOpenModal(false);
                    }}
                >
                    {translate("global.accept")}
                </button>
            ],
            true
        );
    };
    
    const showErrorModal = () => {
        modalContext.confirm(
            translate("global.alerts.ops"),
            <span>{translate("dashboard.packages.search.ERROR_SEARCH")}</span>,
            [
                <button
                    key="accept"
                    type="button"
                    variant="outline-secondary" size="sm"
                    onClick={() => {
                        modalContext.setIsOpenModal(false);
                    }}
                >
                    {translate("global.accept")}
                </button>
            ],
            true
        );
    };
    

    const getDataDefault = async (pageNum = 1) => {
        if (!canSearch) return;
        let dataToSend = {
            estatusBoxit: statusPkgDefault.map((item) => item).join(","),
            itemsPerPage,
            pageNumber: pageNum,
        }

        if (!userContext.user?.IsAdmin) {
            dataToSend.IdUsuario = Number(userContext.user?.Ruc);
        }

        // Establecer los estados por defecto como seleccionados
        const defaultStatusStr = statusPkgDefault.join(',');
        setStatusPkgSelected([...statusPkgDefault]);
        setSelectedStatus(defaultStatusStr);
        lastSearchPayloadRef.current = { ...dataToSend };

        try {
            setIsLoading(true);
            const response = await fetch(`${getClientDomain(2)}/dashboard/packages/find/tracking` , {
                method: 'POST',
                connection: 'keep-alive',
                accept: 'application/json',
                headers: {
                    'Content-Type': 'application/json',
                    'authorization': `Bearer ${getToken()}`
                },
                body: JSON.stringify(dataToSend)
            });
            const result = await response.json();

            if (result.code === 200 && result.payload) {
                setPackagesDefault(result.payload);
                setItemsFiltered(result.payload);
                setPackagesFound(result.payload);
                setCurrentPage(pageNum);
                const fallback = result.payload.length < itemsPerPage ? (pageNum - 1) * itemsPerPage + result.payload.length : pageNum * itemsPerPage;
                setTotalItems(result.totalItems != null ? Number(result.totalItems) : fallback);
                setStatusCounts(result.statusCounts || {});
            } else {
                setPackagesDefault([]);
                setItemsFiltered([]);
                setPackagesFound([]);
                setTotalItems(0);
            }
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    }
            
    const handleCancelBusqueda = () => {
        setPackagesFound([]);
        setItemsFiltered([]);
        setFilters({});
        setSelectedField('numeroTracking');
        setSelectedStatus('all');
        // Restaurar estados por defecto
        setStatusPkgSelected([...statusPkgDefault]);
        setCurrentPage(1);
        setItemsPerPage(20);
        setTotalItems(0);
        setStatusCounts({});
        syncFiltersToUrl({ filters: {}, currentPage: 1, selectedField: 'numeroTracking', selectedStatus: 'all', itemsPerPage: 20 });
        getDataDefault(1);
    };

    // Cargar opciones de estatus
    useEffect(() => {
        if (!canSearch) return;
        const getEstatusBoxit = async () => {
            const storedData = sessionStorage.getItem('status-packages');
            
            if (storedData) {
                const parsed = JSON.parse(storedData);
                setStatusOptions(parsed.map(s => ({
                    value: s.IdEstatus.toString(),
                    label: s.EstatusBoxit
                })));
                return;
            }
            
            try {
                const response = await fetch(`${getClientDomain(2)}/dashboard/packages/estatus/boxit`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'authorization': `Bearer ${getToken()}`
                    }
                });
                
                const result = await response.json();
                if (result?.payload) {
                    const translated = setStatusObjTranslate(result.payload);
                    sessionStorage.setItem('status-packages', JSON.stringify(translated));
                    setStatusOptions(translated.map(s => ({
                        value: s.IdEstatus.toString(),
                        label: s.EstatusBoxit
                    })));
                }
            } catch (error) {
                console.error(error);
            }
        };
        
        getEstatusBoxit();
    }, []);

    // Parsear URL al montar y restaurar filtros si existen
    useEffect(() => {
        if (!canSearch) return;
        const params = new URLSearchParams(location.search);
        const f = params.get('f');
        if (f) {
            try {
                const parsedFilters = JSON.parse(decodeURIComponent(f));
                const page = parseInt(params.get('page') || '1', 10);
                const limit = parseInt(params.get('limit') || '20', 10);
                const field = params.get('field') || 'numeroTracking';
                const status = params.get('status') || (parsedFilters.estatusBoxit ? parsedFilters.estatusBoxit.toString() : 'all');
                setFilters(parsedFilters);
                setCurrentPage(page);
                setItemsPerPage(limit);
                setSelectedField(field);
                setSelectedStatus(status);
                if (parsedFilters.estatusBoxit) {
                    const arr = parsedFilters.estatusBoxit.toString().split(',').map(x => parseInt(x.trim(), 10)).filter(n => !isNaN(n));
                    setStatusPkgSelected(arr);
                    // Actualizar selectedStatus con el valor de estatusBoxit
                    setSelectedStatus(parsedFilters.estatusBoxit.toString());
                } else {
                    // Si no hay estatus en los filtros, usar los estados por defecto
                    const defaultStatusStr = statusPkgDefault.join(',');
                    setStatusPkgSelected([...statusPkgDefault]);
                    setSelectedStatus(defaultStatusStr);
                }
                lastUrlSearchRef.current = location.search ? location.search.slice(1) : '';
                findPackagesWithFilters(parsedFilters, page);
                isInitialMount.current = false;
                return;
            } catch (e) {
                /* ignore parse error */
            }
        }
        getDataDefault();
        isInitialMount.current = false;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const syncFiltersToUrl = (stateToSync) => {
        const f = stateToSync?.filters ?? filters;
        const p = stateToSync?.currentPage ?? currentPage;
        const l = stateToSync?.itemsPerPage ?? itemsPerPage;
        const field = stateToSync?.selectedField ?? selectedField;
        const status = stateToSync?.selectedStatus ?? selectedStatus;
        const hasFilters = Object.keys(f).length > 0;
        if (hasFilters) {
            const params = new URLSearchParams();
            params.set('f', encodeURIComponent(JSON.stringify(f)));
            params.set('page', String(p));
            params.set('limit', String(l));
            params.set('field', field);
            if (status && status !== 'all') params.set('status', status);
            const searchStr = params.toString();
            lastUrlSearchRef.current = searchStr;
            history.replace({ pathname: location.pathname, search: `?${searchStr}` });
        } else {
            lastUrlSearchRef.current = '';
            history.replace({ pathname: location.pathname, search: '' });
        }
    };

    // Restaurar desde URL cuando cambia (ej. botón atrás/adelante del navegador)
    useEffect(() => {
        if (!canSearch) return;
        if (isInitialMount.current) return;
        const searchStr = location.search ? location.search.slice(1) : '';
        if (searchStr === lastUrlSearchRef.current) return;
        const params = new URLSearchParams(location.search);
        const f = params.get('f');
        if (f) {
            try {
                const parsedFilters = JSON.parse(decodeURIComponent(f));
                const page = parseInt(params.get('page') || '1', 10);
                const limit = parseInt(params.get('limit') || '20', 10);
                const field = params.get('field') || 'numeroTracking';
                const status = params.get('status') || (parsedFilters.estatusBoxit ? parsedFilters.estatusBoxit.toString() : 'all');
                setFilters(parsedFilters);
                setCurrentPage(page);
                setItemsPerPage(limit);
                setSelectedField(field);
                setSelectedStatus(status);
                if (parsedFilters.estatusBoxit) {
                    const arr = parsedFilters.estatusBoxit.toString().split(',').map(x => parseInt(x.trim(), 10)).filter(n => !isNaN(n));
                    setStatusPkgSelected(arr);
                    // Actualizar selectedStatus con el valor de estatusBoxit
                    setSelectedStatus(parsedFilters.estatusBoxit.toString());
                } else {
                    // Si no hay estatus en los filtros, usar los estados por defecto
                    const defaultStatusStr = statusPkgDefault.join(',');
                    setStatusPkgSelected([...statusPkgDefault]);
                    setSelectedStatus(defaultStatusStr);
                }
                lastUrlSearchRef.current = searchStr;
                findPackagesWithFilters(parsedFilters, page);
            } catch (e) { /* ignore */ }
        } else {
            setFilters({});
            // Restaurar estados por defecto cuando no hay filtros en URL
            const defaultStatusStr = statusPkgDefault.join(',');
            setStatusPkgSelected([...statusPkgDefault]);
            setSelectedStatus(defaultStatusStr);
            lastUrlSearchRef.current = '';
            getDataDefault();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    // La selección de estatus solo actualiza filtros y URL.
    // La búsqueda se ejecuta cuando el usuario hace clic en el botón \"Search\".

    // Funciones para manejar búsqueda y filtros (modo unificado como Companies)
    const handleSearch = (term, selectedField = 'numeroTracking') => {
        setSelectedField(selectedField);
        setCurrentPage(1);
        if (term && term.trim()) {
            const trimmedTerm = term.trim().toUpperCase();
            const newFilters = { ...filters, [selectedField]: trimmedTerm };
            setFilters(newFilters);
            syncFiltersToUrl({ filters: newFilters, currentPage: 1, selectedField });
        }
    };

    const handleFilter = (newFilters) => {
        setFilters(newFilters);
        setCurrentPage(1);
        const status = newFilters.estatusBoxit || 'all';
        setSelectedStatus(status);
        if (status !== 'all') {
            setStatusPkgSelected(status.toString().split(',').map(x => parseInt(x.trim(), 10)).filter(n => !isNaN(n)));
        } else {
            setStatusPkgSelected([]);
        }
        syncFiltersToUrl({ filters: newFilters, currentPage: 1, selectedField, selectedStatus: status });
        if (Object.keys(newFilters).length > 0) {
            findPackagesWithFilters(newFilters);
        } else {
            handleCancelBusqueda();
        }
    };

    const handleIntegratedFilterChange = (newFilters) => {
        setFilters(newFilters);
        setCurrentPage(1);
        const status = newFilters.estatusBoxit || selectedStatus;
        if (status !== selectedStatus) setSelectedStatus(status || 'all');
        if (status && status !== 'all') {
            setStatusPkgSelected(status.toString().split(',').map(x => parseInt(x.trim(), 10)).filter(n => !isNaN(n)));
        } else {
            setStatusPkgSelected([]);
        }
        syncFiltersToUrl({ filters: newFilters, currentPage: 1, selectedField, selectedStatus: status || 'all' });
        if (Object.keys(newFilters).length > 0) {
            findPackagesWithFilters(newFilters);
        } else {
            getDataDefault(1);
        }
    };

    const updateStatusFilter = (status) => {
        setCurrentPage(1);

        if (status && status !== 'all') {
            const newFilters = { ...filters, estatusBoxit: status };
            setFilters(newFilters);
            setSelectedStatus(status);

            const parsedStatusIds = status
                .toString()
                .split(',')
                .map(x => parseInt(x.trim(), 10))
                .filter(n => !Number.isNaN(n));
            setStatusPkgSelected(parsedStatusIds);

            syncFiltersToUrl({ filters: newFilters, currentPage: 1, selectedField, selectedStatus: status });
            return newFilters;
        }

        // Cuando se limpia el filtro, restaurar estados por defecto
        const newFilters = { ...filters };
        delete newFilters.estatusBoxit;
        setFilters(newFilters);
        const defaultStatusStr = statusPkgDefault.join(',');
        setStatusPkgSelected([...statusPkgDefault]);
        setSelectedStatus(defaultStatusStr);
        syncFiltersToUrl({ filters: newFilters, currentPage: 1, selectedField, selectedStatus: defaultStatusStr });
        return newFilters;
    };

    const handleStatusChange = (status) => {
        // Solo actualizar filtros y estado visual; la búsqueda se hará al presionar \"Search\".
        updateStatusFilter(status);
    };

    const handleStatusClick = (statusId) => {
        // Selección única: si el mismo status ya está seleccionado solo, restaurar los 3 por defecto
        // Si está seleccionado junto con otros, seleccionar solo este
        if (statusPkgSelected.length === 1 && statusPkgSelected.includes(statusId)) {
            // Si solo este está seleccionado, restaurar los 3 por defecto
            const updatedFilters = updateStatusFilter('all');
            // Ejecutar búsqueda inmediatamente con los estados por defecto
            findPackagesWithFilters(updatedFilters, 1);
            return;
        }

        // Seleccionar solo este estado (selección única)
        const statusStr = statusId.toString();
        const updatedFilters = updateStatusFilter(statusStr);
        // Ejecutar búsqueda inmediatamente
        findPackagesWithFilters(updatedFilters, 1);
    };

    const findPackagesWithFilters = async (filterObj, pageNum = 1) => {
        const dataToSend = { ...filterObj };
        
        // Si no hay estatus explícito pero sí una búsqueda por campo (tracking, email, etc.)
        // o un filtro de locker / compañía / tipo de paquete, enviar TODOS los estatus
        // para que el backend no limite a los 3 por defecto.
        if (!dataToSend.estatusBoxit || dataToSend.estatusBoxit === '') {
            const searchKeys = [
                'numeroTracking',
                'numeroPaquete',
                'email',
                'nombre',
                'apellido',
                'externalId',
                'reservationId',
                'idPaquete',
                'plataforma',
                'usuario',
                'typePkg'
            ];
            const hasSearchField = searchKeys.some((key) => {
                const value = dataToSend[key];
                return value !== undefined && value !== null && value !== '';
            });

            if (hasSearchField) {
                // Construir lista de TODOS los estatus disponibles.
                // 1) Preferir lo que ya está en estado (statusOptions)
                // 2) Si aún no está cargado (ej. recarga de página), usar sessionStorage
                let allStatusIdsArray = [];

                if (statusOptions.length) {
                    allStatusIdsArray = statusOptions.map((s) => s.value).filter(Boolean);
                } else {
                    const stored = sessionStorage.getItem('status-packages');
                    if (stored) {
                        try {
                            const parsed = JSON.parse(stored);
                            allStatusIdsArray = parsed
                                .map((s) => (s.IdEstatus ?? s.value ?? '').toString())
                                .filter(Boolean);
                        } catch (e) {
                            // ignore JSON error y no forzar estatus
                        }
                    }
                }

                if (allStatusIdsArray.length) {
                    dataToSend.estatusBoxit = allStatusIdsArray.join(',');
                }
            }
        }
        
        // Limpiar valores vacíos
        Object.keys(dataToSend).forEach(key => {
            if (dataToSend[key] === '' || dataToSend[key] === undefined || dataToSend[key] === null) {
                delete dataToSend[key];
            }
        });
        dataToSend.pageNumber = pageNum;
        await findPackages(dataToSend);
    };

    // Opciones para el selector de campo (modo unificado)
    const fieldOptions = [
        {
            value: 'numeroTracking',
            label: translate('global.numTracking')
        },
        {
            value: 'numeroPaquete',
            label: translate('global.numPackage')
        },
        {
            // Mismo label que la columna de la tabla, para que se lean como lo mismo.
            value: 'idPaquete',
            label: `ID ${translate('global.Package')}`
        },
        {
            value: 'email',
            label: translate('global.email')
        },
        {
            value: 'nombre',
            label: translate('global.name')
        },
        {
            value: 'apellido',
            label: translate('login.signup.lastname')
        }
    ];

    // Agregar campos adicionales según país
    if (!verifyHideCountryItems(["us"])) {
        fieldOptions.push(
            {
                value: 'externalId',
                label: translate('global.ExternalID')
            },
            {
                value: 'reservationId',
                label: translate('global.reservationId')
            }
        );
    }

    // Opciones para el filtro de estatus (con iconos y traducción)
    const statusFilterOptions = [
        {
            value: 'all',
            label: translate('dashboard.clients.filters.allStatus') || 'Todos los estados',
            icon: <FaIcons.FaList size={16} />,
            showLabel: true,
        },
        ...statusOptions.map(status => {
            const statusProperty = getPropertyPackagesStatus(parseInt(status.value));
            return {
                value: status.value,
                label: statusProperty?.text || status.label, // Usar el texto traducido de getPropertyPackagesStatus
                icon: statusProperty?.icon || <FaIcons.FaBox size={16} />,
                showLabel: true,
            };
        })
    ];

    // Función para formatear el valor de los chips mostrando texto en lugar de ID
    const formatChipValue = (field, value) => {
        if (!value) return value;

        // Estatus/Status (soporta multi-selección)
        if (field === 'estatusBoxit') {
            const raw = value.toString();

            // Si hay varios estatus seleccionados (ej: "1,2,3")
            if (raw.includes(',')) {
                const ids = raw
                    .split(',')
                    .map(v => v.trim())
                    .filter(Boolean);

                const labels = ids.map(idStr => {
                    const idNum = parseInt(idStr, 10);
                    const statusProperty = getPropertyPackagesStatus(idNum);
                    if (statusProperty && statusProperty.text) {
                        return statusProperty.text;
                    }
                    const statusOpt = statusOptions.find(s => s.value === idStr);
                    return statusOpt?.label || idStr;
                }).filter(Boolean);

                // Mostrar todos separados por coma, en orden
                return labels.join(', ');
            }

            // Un solo estatus
            const statusProperty = getPropertyPackagesStatus(parseInt(raw, 10));
            if (statusProperty && statusProperty.text) {
                return statusProperty.text;
            }
            // Fallback: buscar en statusOptions si getPropertyPackagesStatus no retorna nada
            const status = statusOptions.find(s => s.value === raw);
            if (status && status.label) {
                return status.label;
            }
            return raw;
        }

        // Tipo de paquete (USA: 0,1 | Panama/BR: 0,1,2, 3)
        if (field === 'typePkg') {
            const packageTypes = {
                '0': translate('global.status.typePackage.regular'),
                '1': translate('global.status.typePackage.returned'),
                'dropoff': translate('global.status.typePackage.dropoff'),
                '0,1,2': translate('global.status.typePackage.regular'),
                '3': translate('global.status.typePackage.returned')
            };
            return packageTypes[value?.toString()] || value;
        }

        // Usuario/Compañía
        if (field === 'usuario') {
            const company = companiesList.find(c => c.IdUsuario?.toString() === value.toString());
            if (company) {
                return `${company.IdUsuario} - ${company.NombreEmpresa}`;
            }
            return value;
        }

        // Plataforma
        if (field === 'plataforma') {
            const valueUpper = value?.toString().toUpperCase();
            const platform = platformsList.find(p => 
                (p.DescPlataforma?.toUpperCase() === valueUpper) ||
                (p.boxitLocationId?.toString() === value.toString()) ||
                (p.locationName?.toUpperCase() === valueUpper)
            );
            if (platform) {
                return platform.DescPlataforma || platform.locationName || value;
            }
            return value;
        }

        // Para otros campos, devolver el valor tal cual
        return value;
    };

    // Función para obtener el label del campo (para chips)
    const getFieldLabelForChip = (field) => {
        if (field === 'estatusBoxit') {
            return translate('global.status') || 'Status';
        }
        if (field === 'plataforma') {
            return translate('dashboard.platforms.name') || 'Plataforma';
        }
        if (field === 'usuario') {
            return translate('global.business') || 'Empresa';
        }
        if (field === 'typePkg') {
            return translate('global.typeOfPackage') || 'Tipo de Paquete';
        }
        const option = fieldOptions.find(opt => opt.value === field);
        return option ? option.label : field;
    };

    // Cargar plataformas y compañías para formatear chips
    useEffect(() => {
        const loadReferenceData = async () => {
            const abortController = new AbortController();
            
            // Cargar plataformas
            try {
                const response = await fetch(`${getClientDomain(2)}/dashboard/platforms/client`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'authorization': `Bearer ${getToken()}`
                    },
                    signal: abortController.signal
                });
                const result = await response.json();
                if (result?.payload?.length > 0) {
                    setPlatformsList(result.payload);
                }
            } catch (error) {
                console.error('Error loading platforms:', error);
            }

            // Cargar compañías (disponible para todos los roles)
            try {
                const response = await fetch(`${getClientDomain(2)}/users/tipoUsuario/all`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'authorization': `Bearer ${getToken()}`
                    },
                    signal: abortController.signal
                });
                const result = await response.json();
                if (result?.payload) {
                    setCompaniesList(result.payload);
                }
            } catch (error) {
                console.error('Error loading companies:', error);
            }

            return () => abortController.abort();
        };

        loadReferenceData();
    }, []);

    // Mapeo de IDs de columnas a keys de datos
    const columnIdToKeyMap = {
        'packageNum': 'Nro_Id',
        'tracking': 'NumeroTracking',
        'customer': 'Cliente',
        'packageId': 'IdPaquete',
        'pkgStatus': 'IdEstatus',
        'pkgType': 'IdTipoPaquete',
        'location': 'Plataforma',
        'doorNum': 'Identificacion',
        'pkgWeight': 'PesoLibras',
        'dateCreatePkg': 'FechaCreacion',
        'dateExpiredPkg': 'FechaExpiracion',
        'totalCost': 'Precio',
        'sizePkg': 'Clasificacion',
        'clientId': 'IdCliente',
        'email': 'Email'
    };

    // Mapeo de IDs de columnas a claves de traducción para los nombres
    const columnNameTranslationMap = {
        'packageId': () => `ID ${translate("global.Package")}`,
        'tracking': () => translate("global.numTracking"),
        'packageNum': () => translate("global.numPackage"),
        'customer': () => translate("global.client"),
        'pkgStatus': () => translate("global.status"),
        'location': () => translate("global.ubication"),
        'doorNum': () => translate("global.numDoor"),
        'pkgWeight': () => translate("global.weight"),
        'dateCreatePkg': () => translate("global.dateCreated"),
        'dateExpiredPkg': () => translate("global.dateExpiration"),
        'totalCost': () => translate("dashboard.packages.loadpackages.formProvider.costTotal"),
        'sizePkg': () => translate("global.size"),
        'clientId': () => `ID ${translate("global.client")}`,
        'email': () => translate("global.email"),
        'pkgType': () => translate("global.typeOfPackage"),
    };

    // Función para convertir hex a rgba
    const hexToRgba = (hex, alpha = 0.15) => {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    // Configurar columnas para la tabla usando useColumnConfig
    const tableColumns = visibleColumns.map(col => {
        const dataKey = columnIdToKeyMap[col.id] || col.id;
        const isTrackingColumn = col.id === 'tracking';
        const isDropOffColumn =
            /drop ?off/i.test(col.id || '') ||
            /drop ?off/i.test(col.name || '');

        let render = (row) => {
            // Manejo especial para pkgStatus
            if (col.id === 'pkgStatus') {
                if (row?.IdEstatus) {
                    const status = getPropertyPackagesStatus(row.IdEstatus);
                    const bgColor = status.color ? hexToRgba(status.color, 0.15) : 'rgba(107, 114, 128, 0.15)';
                    // Para tipo devolución retirado (estatus 4) se muestra "Collected"; el resto conserva el texto estándar.
                    const statusLabel = getPackageDetailStatusLabel(row.IdEstatus, row.IdTipoPaquete) || status.text;
                    return (
                        <Badge
                            variant="neutral"
                            icon={status.icon}
                            showIcon={true}
                            style={{
                                color: status.color || '#6b7280',
                                backgroundColor: bgColor,
                                borderColor: status.color || '#e5e7eb'
                            }}
                        >
                            {statusLabel}
                        </Badge>
                    );
                }
            }

            // Manejo especial para pkgType (icono y color como en el detalle)
            if (col.id === 'pkgType') {
                // Drop Off: IsDropoff = 1 Y IdTipoPaquete = 1 (solo USA). Debe coincidir con la
                // definición del SP UspFindTrackingPaquetes para que el badge y el filtro sean consistentes.
                if (row?.IsDropoff && Number(row?.IdTipoPaquete) === 1) {
                    const dropOffColor = '#4abfee';
                    return (
                        <Badge
                            variant="neutral"
                            icon={<FaIcons.FaBoxOpen />}
                            showIcon={true}
                            style={{
                                color: dropOffColor,
                                backgroundColor: hexToRgba(dropOffColor, 0.15),
                                borderColor: dropOffColor
                            }}
                        >
                            {translate('global.status.typePackage.dropoff')}
                        </Badge>
                    );
                }
                if (row?.IdTipoPaquete == null) return '';
                const typeInfo = getPropsTypeOfPackage(row.IdTipoPaquete);
                if (!typeInfo?.text) return '';
                const bgColor = typeInfo.bg || (typeInfo.color ? hexToRgba(typeInfo.color, 0.15) : 'rgba(107, 114, 128, 0.15)');
                return (
                    <Badge
                        variant="neutral"
                        icon={typeInfo.icon}
                        showIcon={!!typeInfo.icon}
                        style={{
                            color: typeInfo.color || '#6b7280',
                            backgroundColor: bgColor,
                            borderColor: typeInfo.color ? hexToRgba(typeInfo.color, 0.4) : '#e5e7eb'
                        }}
                    >
                        {translate(typeInfo.text)}
                    </Badge>
                );
            }

            // Manejo especial para columnas con botón de copiar (Tracking y Drop Off Code)
            if ((isTrackingColumn || isDropOffColumn) && row) {
                const rawValue = row?.[dataKey];
                const value = rawValue || '-';

                const handleCopy = async (event) => {
                    event.stopPropagation();
                    if (!rawValue) return;
                    try {
                        if (navigator.clipboard && navigator.clipboard.writeText) {
                            await navigator.clipboard.writeText(String(rawValue));
                        } else {
                            // Fallback simple
                            const tempInput = document.createElement('input');
                            tempInput.value = String(rawValue);
                            document.body.appendChild(tempInput);
                            tempInput.select();
                            document.execCommand('copy');
                            document.body.removeChild(tempInput);
                        }
                        showSuccess(translate('global.copied') || 'Copiado al portapapeles', 3000);
                    } catch (e) {
                        showError(translate('global.errorCopy') || 'No se pudo copiar el texto', 3000);
                    }
                };

                return (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            maxWidth: isTrackingColumn ? '220px' : '180px',
                        }}
                    >
                        <span
                            title={value}
                            style={{
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                // Shrink to the text, never grow. With `flex: 1`
                                // the value stretched across the whole cell and
                                // pushed the copy button to the far edge, so the
                                // icon floated away from the value it copies.
                                flex: '0 1 auto',
                                minWidth: 0,
                            }}
                        >
                            {value}
                        </span>
                        {rawValue && (
                            <button
                                type="button"
                                className={styles.copyIconButton}
                                onClick={handleCopy}
                                onMouseDown={(e) => e.stopPropagation()}
                                aria-label={
                                    isTrackingColumn
                                        ? translate('global.copyTracking') || 'Copiar tracking'
                                        : translate('global.copyDropOffCode') || 'Copiar Drop Off Code'
                                }
                            >
                                <FaIcons.FaRegCopy size={14} />
                            </button>
                        )}
                    </div>
                );
            }

            // Usar renderer si existe
            if (renderers[col.id]) {
                return renderers[col.id](row);
            }
            
            // Fallback a valor directo
            return row[dataKey] || '-';
        };

        // Agregar ellipsis para columnas de texto
        if (['packageNum', 'tracking', 'customer', 'location'].includes(col.id)) {
            const originalRender = render;
            render = (row) => {
                const value = originalRender(row);
                if (typeof value === 'string') {
                    return (
                        <span 
                            title={value}
                            style={{
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: col.id === 'customer' ? '200px' : col.id === 'tracking' ? '180px' : '150px'
                            }}
                        >
                            {value}
                        </span>
                    );
                }
                return value;
            };
        }

        // Traducir el nombre de la columna en tiempo de renderizado
        const getColumnLabel = () => {
            const translateFn = columnNameTranslationMap[col.id];
            if (translateFn) {
                return translateFn();
            }
            // Fallback al nombre guardado si no hay traducción
            return col.name;
        };

        return {
            key: dataKey,
            label: getColumnLabel(),
            sortable: ['packageNum', 'tracking'].includes(col.id),
            // Tabular but NOT right-aligned. Right alignment is for quantities
            // you compare down the column; a package id is an identifier — a
            // name made of digits — and pushing it away from the row's leading
            // edge just opens a gap. Tabular figures still keep the digits even.
            numeric: col.id === 'packageId',
            width: col.id === 'customer' ? '200px' : col.id === 'tracking' ? '180px' : col.id === 'pkgStatus' ? '150px' : '150px',
            render
        };
    });

    const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
        syncFiltersToUrl({ currentPage: newPage });
        const last = lastSearchPayloadRef.current;
        if (last) {
            setIsLoading(true);
            const payload = { ...last, pageNumber: newPage, itemsPerPage };
            lastSearchPayloadRef.current = payload;
            fetch(`${getClientDomain(2)}/dashboard/packages/find/tracking`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    authorization: `Bearer ${getToken()}`
                },
                body: JSON.stringify(payload)
            })
                .then(res => res.json())
                .then(result => {
                    if (result.code === 200 && result.payload) {
                        setPackagesFound(result.payload);
                        setItemsFiltered(result.payload);
                        const fallback = result.payload.length < itemsPerPage ? (newPage - 1) * itemsPerPage + result.payload.length : newPage * itemsPerPage;
                        setTotalItems(result.totalItems != null ? Number(result.totalItems) : fallback);
                        setStatusCounts(result.statusCounts || {});
                    }
                })
                .catch(err => console.error(err))
                .finally(() => setIsLoading(false));
        }
    };

    const handleItemsPerPageChange = (newItemsPerPage) => {
        setItemsPerPage(newItemsPerPage);
        setCurrentPage(1);
        syncFiltersToUrl({ itemsPerPage: newItemsPerPage, currentPage: 1 });
        const last = lastSearchPayloadRef.current;
        if (last) {
            setIsLoading(true);
            lastSearchPayloadRef.current = { ...last, pageNumber: 1, itemsPerPage: newItemsPerPage };
            fetch(`${getClientDomain(2)}/dashboard/packages/find/tracking`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    authorization: `Bearer ${getToken()}`
                },
                body: JSON.stringify({ ...last, pageNumber: 1, itemsPerPage: newItemsPerPage })
            })
                .then(res => res.json())
                .then(result => {
                    if (result.code === 200 && result.payload) {
                        setPackagesFound(result.payload);
                        setItemsFiltered(result.payload);
                        const fallback = result.payload.length < newItemsPerPage ? result.payload.length : newItemsPerPage;
                        setTotalItems(result.totalItems != null ? Number(result.totalItems) : fallback);
                        setStatusCounts(result.statusCounts || {});
                    }
                })
                .catch(err => console.error(err))
                .finally(() => setIsLoading(false));
        }
    };

    // Acciones del header con SelectorColumns
    const headerActions = (
        <div className={styles.headerActions}>
            <SelectorColumns
                columns={columns}
                setColumns={setColumns}
                isVisible={true}
                mode="packages"
            />
        </div>
    );

    // Subtitle con total y badges inline
    const headerSubtitle = (
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
            <span>{(totalItems || packagesFound.length)?.toLocaleString('en-US')} {translate("global.total")}</span>
            <StatusSummaryCards
                packagesDefault={packagesDefault}
                statusCounts={statusCounts}
                statusPkgSelected={statusPkgSelected}
                onStatusClick={handleStatusClick}
                onMoreStatusClick={() => setIsStatusPanelOpen(true)}
            />
        </div>
    );

    const handleRowClick = (row) => {
        if (!canSearch) return;
        const detailPath = `${location.pathname}/package/${row.IdPaquete}`;
        history.push(detailPath, { fromSearch: location.search || undefined });
    };

    if (!canSearch) {
        const rawMsg = translate("dashboard.clients.formSearch.NOT_ACCESS");
        const msg =
            rawMsg && rawMsg !== "dashboard.clients.formSearch.NOT_ACCESS"
                ? rawMsg
                : "No tienes acceso para ver esta información.";

        return (
            <PageContainer>
                <PageHeader
                    title={`${translate("sidebar.options.packages.tracking")} ${translate("global.packages")}`}
                    icon={<FaBox size={24} />}
                />
                <ContentCard padding="md">
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                        <div
                            style={{
                                width: 40,
                                height: 40,
                                borderRadius: 12,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: 'rgba(107,114,128,0.12)',
                                color: '#374151',
                                flex: '0 0 auto',
                            }}
                            aria-hidden
                        >
                            <FaIcons.FaLock size={18} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 700, fontSize: 16, color: '#111827' }}>
                                {translate("global.unauthorized") !== "global.unauthorized"
                                    ? translate("global.unauthorized")
                                    : "Acceso restringido"}
                            </div>
                            <div style={{ marginTop: 4, color: '#6B7280' }}>
                                {msg}
                            </div>
                            <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                <Button
                                    variant="primary"
                                    type="button"
                                    onClick={() => history.push('/dashboard')}
                                >
                                    {translate("global.goBack") !== "global.goBack" ? translate("global.goBack") : "Volver"}
                                </Button>
                            </div>
                        </div>
                    </div>
                </ContentCard>
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            <ToastContainer toasts={toasts} removeToast={removeToast} />
            <PageHeader
                title={`${translate("sidebar.options.packages.tracking")} ${translate("global.packages")}`}
                subtitle={headerSubtitle}
                icon={<FaBox size={24} />}
                actions={headerActions}
            />

            {/* Filtros integrados: Plataforma, Compañía y Tipo de Paquete */}
            <IntegratedFilter
                onFilterChange={handleIntegratedFilterChange}
                activeFilters={filters}
            />

            {/* Filtros inline - Modo unificado como Companies */}
            <SearchFilter
                placeholder={translate('global.search') + '...'}
                onSearch={handleSearch}
                onFilter={handleFilter}
                onClear={handleCancelBusqueda}
                activeFilters={filters}
                loading={isLoading}
                unifiedMode={true}
                selectedField={selectedField}
                onFieldChange={setSelectedField}
                fieldOptions={fieldOptions}
                formatChipValue={formatChipValue}
                formatChipLabel={getFieldLabelForChip}
            />

            {isStatusPanelOpen && (
                <StatusFilterPanel
                    statusOptions={statusFilterOptions.filter(opt => opt.value !== 'all')}
                    value={
                        selectedStatus === 'all' 
                            ? (statusPkgSelected.length > 0 ? statusPkgSelected.join(',') : '') 
                            : selectedStatus
                    }
                    onChange={(statusValue) => {
                        // statusValue puede ser string vacío, string con comas, o 'all'
                        const normalized = statusValue || 'all';
                        updateStatusFilter(normalized);
                    }}
                    onClose={() => setIsStatusPanelOpen(false)}
                />
            )}

            {/* Resultados */}
            <section ref={sectionResults}>
                {/* The table draws its own skeleton: swapping it for a bare
                    spinner meant `loading` never reached it, and the column
                    headers vanished during the wait so the layout jumped when
                    the rows landed. */}
                <Table
                    columns={tableColumns}
                    data={packagesFound}
                    loading={isLoading}
                    emptyMessage={translate("global.noResults")}
                    onRowClick={handleRowClick}
                    hoverable
                />

                {/* Paginación */}
                {!isLoading && packagesFound.length > 0 && (
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalItems={totalItems}
                        itemsPerPage={itemsPerPage}
                        onPageChange={handlePageChange}
                        onItemsPerPageChange={handleItemsPerPageChange}
                        showItemsPerPage={true}
                    />
                )}
            </section>
        </PageContainer>
    );
}

export default SearchPackages;